import { useEffect, useRef, type ReactNode } from "react";
import { motion, type Variants } from "framer-motion";
import { DUR_SNAP, DUR_SETTLE, EASE_PRODUCT } from "@/lib/motion";

interface DialogProps {
  titleId: string;
  onClose: () => void;
  children: ReactNode;
  /** "modal" (centralizado) ou "drawer" (lateral). */
  variant?: "modal" | "drawer";
  wide?: boolean;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Identidade de movimento do produto (ver src/lib/motion.ts): ritmo
// "assentamento" (360ms) pra entrada de modal e drawer, "snap" (180ms) pra
// saida/overlay. MotionConfig reducedMotion="user" (ver main.tsx) ja
// neutraliza isso sozinho quando o sistema pede menos movimento — nao
// precisa checar aqui.
const overlayVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DUR_SNAP, ease: EASE_PRODUCT } },
  exit: { opacity: 0, transition: { duration: DUR_SNAP, ease: EASE_PRODUCT } },
};

const modalVariants: Variants = {
  hidden: { opacity: 0, y: 12, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: DUR_SETTLE, ease: EASE_PRODUCT } },
  exit: { opacity: 0, y: 12, scale: 0.97, transition: { duration: DUR_SNAP, ease: EASE_PRODUCT } },
};

const drawerVariants: Variants = {
  hidden: { opacity: 0, x: 28 },
  visible: { opacity: 1, x: 0, transition: { duration: DUR_SETTLE, ease: EASE_PRODUCT } },
  exit: { opacity: 0, x: 28, transition: { duration: DUR_SNAP, ease: EASE_PRODUCT } },
};

/** Overlay acessivel compartilhado por Modal e Drawer: Escape fecha, foco
 * preso dentro do dialogo (Tab/Shift+Tab), foco inicial no dialogo e foco
 * devolvido ao elemento que abriu ao fechar.
 *
 * Pensado pra ser renderizado condicionalmente (`{aberto && <Dialog/>}`)
 * dentro de um `<AnimatePresence>` no componente pai — isso da a animacao
 * de SAIDA de verdade (o componente so desmonta depois do `exit` terminar),
 * sem exigir nenhuma mudanca na logica de abrir/fechar de cada tela. */
export function Dialog({ titleId, onClose, children, variant = "modal", wide }: DialogProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    containerRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !containerRef.current) return;

      const focusables = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      previouslyFocused.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // O backdrop fechar no clique-fora e um atalho de MOUSE apenas: o
    // equivalente por teclado (Escape, tratado acima, e o botao de fechar
    // dentro do dialogo) ja cobre o mesmo caso de uso, entao este div nao
    // precisa entrar na ordem de tab nem responder a tecla.
    <motion.div
      className="overlay"
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={variant === "drawer" ? "drawer" : `modal${wide ? " modal--wide" : ""}`}
        variants={variant === "drawer" ? drawerVariants : modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
